// Dispatch — the core design property. Iterate the payload's report entries
// (metadata keys already filtered by listReports) and render by `viz`. A new
// report of a KNOWN viz appears with zero code; a genuinely new viz is skipped,
// not errored.
//
// Two consumers:
//   - renderReports  — one card per report via RENDERERS (the generic
//     dispatch-by-viz proof the tests assert on).
//   - getLayoutSections — the page's actual placement, which arranges reports
//     into three lanes (top / tables / charts) BY VIZ, so genericity holds, and
//     splits net_flow into its KPI tiles (top) and its charts (lower section).
//
// Registered viz: net_flow, ranked (top_flow + most_active + unusual_activity),
// chain, by_expiry, breakdown (composition + session_volume), timeseries (the
// whole Tier-1 family).

import { FC, ReactElement } from 'react';

import {
  BreakdownChart,
  BreakdownReport,
  FlowPayload,
  FlowReport,
  FlowReportMeta,
} from '../../providers/options-flow/types';
import { listReports } from '../../providers/options-flow/options-flow.helpers';
import { NetFlowCard, NetFlowKpis, NetFlowCharts } from './components/net-flow-card';
import { RankedCard } from './components/ranked-card';
import { ChainCard } from './components/chain-card';
import { ByExpiryCard } from './components/by-expiry-card';
import { BreakdownCard, DimOpts, groupCharts, parseChartKey } from './components/breakdown-card';
import { TimeseriesCard } from './components/timeseries-card';
import { VenueComparisonCard } from './components/venue-comparison-card';

// Layout hint for the tables lane: 'full' spans the 2-col grid, 'half' is one
// column. By viz, so it stays data-generic.
export type CardSpan = 'full' | 'half';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RENDERERS: Record<string, FC<{ report: any }>> = {
  net_flow: NetFlowCard,
  ranked: RankedCard,
  chain: ChainCard,
  by_expiry: ByExpiryCard,
  breakdown: BreakdownCard,
  timeseries: TimeseriesCard,
  // Top-level (spans venues), placed by the page as a collapsible panel — not a
  // lane card — but registered so the dispatch-by-viz registry stays complete.
  venue_comparison: VenueComparisonCard,
};

// One card per report, in payload order — the thin generic wrapper the
// skip-rule / "new report appears" tests assert on.
export const renderReports = (payload: FlowPayload | null): ReactElement[] =>
  listReports(payload)
    .map(([name, report]: [string, FlowReport]) => {
      const Renderer = RENDERERS[report.viz];
      return Renderer ? <Renderer key={name} report={report} /> : null;
    })
    .filter((el): el is ReactElement => el !== null);

export interface FlowCardEntry {
  name: string;
  viz: string;
  span: CardSpan;
  element: ReactElement;
}

// The composite 2-column region: LEFT the tall top_flow ranked card, RIGHT the
// side-stack breakdowns (Volume by session, then Block vs Screen) stacked.
// Supersedes the old standalone breakdownGrid row.
export interface CompositeRegion {
  ranked: ReactElement | null; // LEFT: the primary (top_flow) ranked explorer
  sideStack: ReactElement[]; // RIGHT: session, then block-vs-screen — stacked
}

export interface LayoutSections {
  top: ReactElement[]; // KPI tiles (full-width)
  composite: CompositeRegion; // ranked (L) + session/block-vs-screen stack (R)
  tables: FlowCardEntry[]; // remaining ranked (unusual) / chain / by_expiry
  charts: ReactElement[]; // net_flow charts, then the timeseries family (stack)
  bottom: ReactElement[]; // the Flow-composition panel — pinned to the very bottom
}

// The primary ranked report that anchors the composite region's LEFT column.
// Other ranked reports (e.g. unusual_activity) stay in the tables lane.
const PRIMARY_RANKED = 'top_flow';

// ---- Breakdown panel assembly (IM-1942) -----------------------------------
// The flow breakdowns are RE-COMPOSED across report boundaries, not rendered
// one-card-per-report. Three panels are assembled from dims that live in
// different reports:
//   Panel A "Flow composition" (full-width, top lane): call_put, taker_side,
//     positioning (from `composition`) + structures (pulled from `structures`).
//     venue_channel is removed from it.
//   Panel B "Volume by session" (side stack): the `session_volume` report, whole.
//   Panel C "Block vs Screen" (side stack): venue_channel, extracted from
//     `composition` into its own panel.
// Every cross-report access is GUARDED: a missing report or dimension drops that
// donut/panel — it never throws (William watches this page live).
const COMPOSITION_REPORT = 'composition';
const STRUCTURES_REPORT = 'structures';
const SESSION_REPORT = 'session_volume';

// Synthetic-panel identity for the extracted venue_channel panel. It has no
// source report of its own (it's carved out of `composition`), so its key/title
// are minted here — exported so tests key off the const, not a duplicated literal.
export const BLOCK_VS_SCREEN_KEY = 'block_vs_screen';
export const BLOCK_VS_SCREEN_TITLE = 'Block vs Screen';
const BLOCK_VS_SCREEN_CAPTION = 'Premium share by execution channel';

// Panel A dimensions IN ORDER (call_put, taker_side, positioning from
// composition; structures appended from its own report).
const COMPOSITION_DIMS = ['call_put', 'taker_side', 'positioning'] as const;
const STRUCTURES_DIM = 'structures';
const VENUE_CHANNEL_DIM = 'venue_channel';

// Per-DIMENSION opts (the seam): structures keeps its dominant single-leg drop,
// tail-fold, and categorical palette while its Panel-A siblings stay on the tone
// palette. The emptyMessage is merged in per-call (it can be venue-specific).
const STRUCTURES_DIM_OPTS: DimOpts = { dropSlice: 'single', topN: 7, categorical: true };

// Header for the reserved (empty) structures slot, and the generic message when
// the venue DOES tag structures but the window is empty. A venue that doesn't
// tag structures supplies its own venue-named message via LayoutOptions.
const STRUCTURES_SLOT_LABEL = 'Structures';
const STRUCTURES_EMPTY_DEFAULT = 'No structures in window';

// Layout-time context the pure payload can't carry — currently just the
// venue-named "structures not tagged" copy for the always-present slot 4.
export interface LayoutOptions {
  structuresEmptyMessage?: string;
}

// Charts of a report whose parsed dimension is in `dims`, emitted in `dims`
// order (so Panel A's donuts render call_put -> taker_side -> positioning ->
// structures regardless of source-report chart order). `channelAllOnly` keeps
// only the base channel — used to pull structures WITHOUT dragging its
// block/screen variants (which would spawn an unwanted channel toggle on Panel A).
const chartsForDims = (
  report: BreakdownReport | undefined,
  dims: readonly string[],
  channelAllOnly = false,
): BreakdownChart[] => {
  const charts = report?.charts;
  if (!charts) return [];
  const out: BreakdownChart[] = [];
  for (const dim of dims) {
    for (const chart of charts) {
      const parsed = parseChartKey(chart.key);
      if (parsed.dimension !== dim) continue;
      if (channelAllOnly && parsed.channel !== 'all') continue;
      out.push(chart);
    }
  }
  return out;
};

// A ready-to-render breakdown panel. `dims` is derived (for ordering/tests);
// the rest feeds a synthetic BreakdownReport + BreakdownCard props.
export interface AssembledPanel {
  key: string;
  title: string;
  meta: FlowReportMeta;
  charts: BreakdownChart[];
  dims: string[];
  dimOpts?: Record<string, DimOpts>;
  caption?: string;
}

export interface BreakdownPanels {
  composition: AssembledPanel | null; // full-width, top lane
  side: AssembledPanel[]; // session, then block-vs-screen — the composite stack
}

// Pure cross-report assembly — element-free so it's unit-testable and the
// dispatch tests can assert composition without rendering.
export const assembleBreakdownPanels = (
  byName: Record<string, BreakdownReport>,
  opts: LayoutOptions = {},
): BreakdownPanels => {
  const composition = byName[COMPOSITION_REPORT];
  const structures = byName[STRUCTURES_REPORT];
  const session = byName[SESSION_REPORT];

  // Structures is ALWAYS the 4th slot of Flow composition (IM-1945): the real
  // base-channel donut when it has data, else a placeholder that renders the
  // empty-state message in-place. It is NEVER a standalone card.
  const structuresCharts = chartsForDims(structures, [STRUCTURES_DIM], true);
  const structuresSlot: BreakdownChart[] =
    structuresCharts.length > 0
      ? structuresCharts
      : [{ key: STRUCTURES_DIM, label: STRUCTURES_SLOT_LABEL, slices: [] }];

  // Panel A is ANCHORED on the composition report: its dims (venue_channel
  // excluded) + the structures slot. No composition report => no Panel A (a lone
  // structures slot must not masquerade as "Flow composition").
  const compositionCharts = composition
    ? [...chartsForDims(composition, COMPOSITION_DIMS), ...structuresSlot]
    : [];
  const compositionPanel: AssembledPanel | null =
    composition && compositionCharts.length > 0
      ? {
          key: COMPOSITION_REPORT,
          title: composition.title,
          // Carry the structured share so the "% structured" caption is accurate
          // now that the structures donut lives in this panel.
          meta: {
            ...composition.meta,
            structured_share_pct:
              structures?.meta?.structured_share_pct ?? composition.meta.structured_share_pct,
          },
          charts: compositionCharts,
          dims: groupCharts(compositionCharts).dimensions,
          // Merge the (possibly venue-named) empty message onto the structures
          // opts; a supported-but-empty window falls back to the generic default.
          dimOpts: {
            [STRUCTURES_DIM]: {
              ...STRUCTURES_DIM_OPTS,
              emptyMessage: opts.structuresEmptyMessage ?? STRUCTURES_EMPTY_DEFAULT,
            },
          },
        }
      : null;

  const side: AssembledPanel[] = [];

  // Panel B: Volume by session — the report rendered whole (single donut).
  if (session?.charts?.length) {
    side.push({
      key: SESSION_REPORT,
      title: session.title,
      meta: session.meta,
      charts: session.charts,
      dims: groupCharts(session.charts).dimensions,
    });
  }

  // Panel C: Block vs Screen — venue_channel extracted from composition.
  const venueChannelCharts = chartsForDims(composition, [VENUE_CHANNEL_DIM]);
  if (composition && venueChannelCharts.length > 0) {
    side.push({
      key: BLOCK_VS_SCREEN_KEY,
      title: BLOCK_VS_SCREEN_TITLE,
      // No structured-share caption on this panel.
      meta: { ...composition.meta, structured_share_pct: undefined },
      charts: venueChannelCharts,
      dims: [VENUE_CHANNEL_DIM],
      caption: BLOCK_VS_SCREEN_CAPTION,
    });
  }

  return { composition: compositionPanel, side };
};

// Build a BreakdownCard from an assembled panel (synthetic breakdown report).
const renderPanel = (panel: AssembledPanel): ReactElement => (
  <BreakdownCard
    key={panel.key}
    report={{ viz: 'breakdown', title: panel.title, meta: panel.meta, charts: panel.charts }}
    dimOpts={panel.dimOpts}
    caption={panel.caption}
  />
);

// Tables-lane span by viz — chain + the grouped by_expiry table are wide.
const TABLE_SPAN: Record<string, CardSpan> = {
  ranked: 'half',
  chain: 'full',
  by_expiry: 'full',
};

// Tables-lane render order by viz (client-feedback IM-1934): Flow-by-Expiry sits
// ABOVE the option-chain. Unlisted viz keep payload order after these.
// `ranked` only reaches the tables lane when SHOW_TIME_CHARTS is on (the
// non-primary unusual_activity card) — unreachable until that flag flips.
const TABLE_VIZ_ORDER: Record<string, number> = {
  by_expiry: 0,
  chain: 1,
  ranked: 2,
};

// v1 scoping (William): the page ends at Flow by Expiry — hide everything below.
// This omits the whole time-charts lane (net_flow's lower charts + the ts family
// + cross-asset) AND the non-primary ranked cards (unusual_activity). The KPI
// tiles (NetFlowKpis) stay. Flip to `true` to restore the full v2 layout — the
// renderers/registry are untouched, they're just not placed. One reversible flag.
const SHOW_TIME_CHARTS = false;

export const getLayoutSections = (
  payload: FlowPayload | null,
  opts: LayoutOptions = {},
): LayoutSections => {
  const tiles: ReactElement[] = [];
  // Breakdown reports are collected here, then RE-COMPOSED across report
  // boundaries by assembleBreakdownPanels (IM-1942) — not routed one-card-per-
  // report. Panel A (composition) goes full-width up top; the session +
  // block-vs-screen panels stack in the composite region's RIGHT column.
  const breakdownReports: Record<string, BreakdownReport> = {};
  let rankedPrimary: ReactElement | null = null;
  const tables: FlowCardEntry[] = [];
  const netFlowCharts: ReactElement[] = [];
  const timeseries: ReactElement[] = [];

  listReports(payload).forEach(([name, report]: [string, FlowReport]) => {
    // most_active is superseded by top_flow's sortable "Top instruments" card
    // (same table sorted by trades) — suppressed here until flow_claude drops it.
    if (name === 'most_active') return;

    switch (report.viz) {
      case 'net_flow':
        // KPI tiles always show; the lower cumulative/daily charts are v2-only.
        tiles.push(<NetFlowKpis key={name} report={report} />);
        if (SHOW_TIME_CHARTS) {
          netFlowCharts.push(<NetFlowCharts key={`${name}-charts`} report={report} />);
        }
        break;
      case 'breakdown':
        // Collect now; re-compose after the loop (dims cross report boundaries).
        breakdownReports[name] = report as BreakdownReport;
        break;
      case 'ranked': {
        // The primary ranked (top_flow) anchors the composite LEFT column; any
        // other ranked (unusual_activity) is a v2-only half card in the tables
        // lane.
        if (name === PRIMARY_RANKED && rankedPrimary === null) {
          rankedPrimary = <RankedCard key={name} report={report} />;
        } else if (SHOW_TIME_CHARTS) {
          tables.push({
            name,
            viz: report.viz,
            span: TABLE_SPAN.ranked,
            element: <RankedCard key={name} report={report} />,
          });
        }
        break;
      }
      case 'chain':
      case 'by_expiry': {
        const Renderer = RENDERERS[report.viz];
        tables.push({
          name,
          viz: report.viz,
          span: TABLE_SPAN[report.viz],
          element: <Renderer key={name} report={report} />,
        });
        break;
      }
      case 'timeseries':
        if (SHOW_TIME_CHARTS) {
          timeseries.push(<TimeseriesCard key={name} report={report} />);
        }
        break;
      // unknown viz -> skipped
    }
  });

  // Re-compose the collected breakdown reports into the three panels.
  const panels = assembleBreakdownPanels(breakdownReports, opts);
  const compositionElement = panels.composition ? renderPanel(panels.composition) : null;
  const sideBreakdownElements = panels.side.map(renderPanel);

  // Stable-sort the tables lane by viz so Flow-by-Expiry renders above the
  // option-chain (Array.prototype.sort is stable; unlisted viz keep their
  // relative payload order via the 99 fallback).
  const orderedTables = [...tables].sort(
    (a, b) => (TABLE_VIZ_ORDER[a.viz] ?? 99) - (TABLE_VIZ_ORDER[b.viz] ?? 99),
  );

  return {
    top: tiles,
    composite: { ranked: rankedPrimary, sideStack: sideBreakdownElements },
    tables: orderedTables,
    charts: [...netFlowCharts, ...timeseries],
    // Flow composition is pinned to the VERY BOTTOM of the page (William, live).
    bottom: compositionElement ? [compositionElement] : [],
  };
};
